import { Hash, Mic, PhoneForwarded, PhoneOff } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { TimelineEventRow } from '@apps/web/components/ui/timeline_event_row';
import { TranscriptTurnTimestamp } from '@apps/web/components/voice_calls/call_transcript_display_utils';
import {
    getDisconnectAttributionLabel,
    isPhoebeDisconnectAttribution,
    type VoiceCallDisconnectAttributionLike,
} from '@apps/web/components/voice_calls/voice_call_disconnect_attribution';
import { type VoiceCallTranscriptViewModel } from '@apps/web/components/voice_calls/voice_call_transcript_card';

// Speaker labels that represent the agency side of the call: Phoebe's voice
// agent, and the back-office "Representative" label that formatSotSpeaker
// emits for post-transfer agent turns. Everything else is the other party on
// the call and is normalized to "Caller" in the UI.
const AGENT_SPEAKER_PATTERN = /^(agent|phoebe|assistant|representative)$/i;
// Post-transfer turns are handled by a human back-office representative, not
// Phoebe, so they keep a distinct label.
const REPRESENTATIVE_SPEAKER_PATTERN = /^representative$/i;

function getSpeakerDisplayLabel(speakerLabel: string): string {
    const normalized = speakerLabel.trim();
    if (!AGENT_SPEAKER_PATTERN.test(normalized)) {
        return 'Caller';
    }
    return REPRESENTATIVE_SPEAKER_PATTERN.test(normalized)
        ? 'Representative'
        : 'Phoebe';
}

// Parse a "M:SS" / "MM:SS" / "H:MM:SS" timestamp label (optionally prefixed
// with "~" for approximate offsets) into seconds, so a turn can seek the
// recording. Returns null when the label is missing or unparsable.
export function parseTimestampLabelToSeconds(
    label: string | null,
): number | null {
    if (!label) {
        return null;
    }
    const parts = label.replace('~', '').trim().split(':');
    if (parts.length < 2 || parts.length > 3) {
        return null;
    }
    const numbers = parts.map((part) => Number(part));
    if (numbers.some((value) => !Number.isFinite(value))) {
        return null;
    }
    return numbers.reduce((total, value) => total * 60 + value, 0);
}

function SectionHeader({
    Icon,
    label,
}: {
    readonly Icon: typeof Mic;
    readonly label: string;
}) {
    return (
        <div className="text-muted-foreground flex items-center gap-2 text-xs font-semibold tracking-wide uppercase">
            <Icon className="h-4 w-4" />
            <span>{label}</span>
        </div>
    );
}

function CallEventRow({
    Icon,
    label,
    tone = 'muted',
    ariaLabel,
}: {
    readonly Icon: typeof PhoneOff;
    readonly label: string;
    readonly tone?: 'muted' | 'danger';
    readonly ariaLabel?: string;
}) {
    return (
        <TimelineEventRow
            ariaLabel={ariaLabel ?? label}
            className="justify-center"
            icon={<Icon aria-hidden="true" />}
            tone={tone === 'danger' ? 'danger' : 'muted'}
        >
            <span className="min-w-0">{label}</span>
        </TimelineEventRow>
    );
}

// Bridged outcomes render at the transfer boundary inside the transcript;
// everything else renders at the end of the call.
const BRIDGED_TRANSFER_OUTCOME_SUMMARIES = new Set([
    'answered',
    'voicemail',
    'connected',
]);

export function formatTransferOutcomePillLabel({
    destinationLabel,
    outcomeSummary,
}: {
    readonly destinationLabel: string | null;
    readonly outcomeSummary: string | null;
}): string | null {
    const destination = destinationLabel;
    switch (outcomeSummary) {
        case 'answered':
            return destination
                ? `Transferred to ${destination} — answered`
                : 'Transfer answered';
        case 'voicemail':
            return destination
                ? `Transferred to ${destination} — reached voicemail`
                : 'Transfer reached voicemail';
        case 'connected':
            return destination
                ? `Transferred to ${destination}`
                : 'Transferred';
        case 'no_answer':
            return destination
                ? `Rang ${destination} — no answer`
                : 'Transfer rang — no answer';
        case 'busy':
            return destination
                ? `${destination} — line busy`
                : 'Transfer line busy';
        case 'canceled':
            return destination
                ? `Caller hung up while ${destination} rang`
                : 'Caller hung up during transfer';
        case 'failed':
            return destination
                ? `Transfer to ${destination} failed`
                : 'Transfer failed';
        default:
            // Calls without an outcome verdict fall back to the legacy
            // labels the caller derives from bridged/attempted state.
            return null;
    }
}

export function VoiceCallsTranscript({
    viewModel,
    disconnectAttribution,
    onSeek,
    afterSummary,
    showSummary = true,
}: {
    readonly viewModel: VoiceCallTranscriptViewModel;
    readonly disconnectAttribution?: VoiceCallDisconnectAttributionLike | null;
    readonly onSeek?: (seconds: number) => void;
    // Optional content rendered between the call summary and the transcript
    // (e.g. the recording player).
    readonly afterSummary?: ReactNode;
    // The call dialog already shows the summary in the thread card, so it
    // renders only the transcript here.
    readonly showSummary?: boolean;
}) {
    const disconnectAttributionLabel = getDisconnectAttributionLabel(
        disconnectAttribution,
    );
    const outcomeSummary = viewModel.transferOutcomeSummary;
    const transferBridged = outcomeSummary
        ? BRIDGED_TRANSFER_OUTCOME_SUMMARIES.has(outcomeSummary)
        : viewModel.attemptedTransferLabel === null;
    const outcomePillLabel = formatTransferOutcomePillLabel({
        destinationLabel: transferBridged
            ? viewModel.transferDestinationLabel
            : (viewModel.attemptedTransferLabel ??
              viewModel.transferDestinationLabel),
        outcomeSummary,
    });
    // Legacy calls without an outcome verdict keep their old two labels:
    // bridged shows the destination at the boundary, unbridged shows the
    // attempted destination with a didn't-connect note.
    const bridgedTransferPillLabel = transferBridged
        ? (outcomePillLabel ??
          (viewModel.transferDestinationLabel
              ? `Transferred to ${viewModel.transferDestinationLabel}`
              : 'Transferred to back office'))
        : null;
    const failedTransferPillLabel = !transferBridged
        ? (outcomePillLabel ??
          (viewModel.attemptedTransferLabel
              ? `${viewModel.attemptedTransferLabel} — didn’t connect`
              : null))
        : null;
    const failedTransferTimelineEvent = failedTransferPillLabel ? (
        <CallEventRow
            Icon={PhoneForwarded}
            label={failedTransferPillLabel}
            ariaLabel={`Transfer attempted: ${failedTransferPillLabel}`}
        />
    ) : null;
    // A bridged verdict normally renders at the transcript's transfer
    // boundary; when no row carries the boundary marker (plain-text or
    // missing SOT transcripts), surface it at the end instead so the outcome
    // is never silently dropped.
    const hasTransferBoundaryRow = viewModel.transcriptRows.some(
        (turn) => turn.isPostTransferStart,
    );
    const bridgedTransferFallbackEvent =
        bridgedTransferPillLabel &&
        outcomeSummary !== null &&
        !hasTransferBoundaryRow ? (
            <CallEventRow
                Icon={PhoneForwarded}
                label={bridgedTransferPillLabel}
            />
        ) : null;
    // The canceled verdict already says the caller hung up mid-ring; a second
    // "Caller hung up" pill would restate it.
    const callEndedTimelineEvent =
        disconnectAttributionLabel &&
        disconnectAttribution &&
        outcomeSummary !== 'canceled' ? (
            <CallEventRow
                Icon={PhoneOff}
                label={disconnectAttributionLabel}
                tone={
                    isPhoebeDisconnectAttribution(disconnectAttribution)
                        ? 'danger'
                        : 'muted'
                }
                ariaLabel={`Call ended: ${disconnectAttributionLabel}`}
            />
        ) : null;

    if (
        !viewModel.hasContent &&
        !callEndedTimelineEvent &&
        !failedTransferTimelineEvent &&
        !bridgedTransferFallbackEvent
    ) {
        return (
            <p className="text-muted-foreground text-sm">
                No recording, summary, or transcript is available for this call.
            </p>
        );
    }

    return (
        <div className="space-y-6">
            {viewModel.hasContent && showSummary ? (
                <section className="space-y-2">
                    <SectionHeader Icon={Mic} label="Call summary" />
                    <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                        {viewModel.summaryText}
                    </p>
                </section>
            ) : null}
            {viewModel.hasContent ? afterSummary : null}
            {viewModel.transcriptRows.length > 0 ? (
                <section aria-label="Transcript" className="space-y-3">
                    <SectionHeader Icon={Hash} label="Transcript" />
                    <div className="space-y-4">
                        {viewModel.transcriptRows.map((turn) => {
                            const speakerDisplayLabel = getSpeakerDisplayLabel(
                                turn.speakerLabel,
                            );
                            const seconds = parseTimestampLabelToSeconds(
                                turn.timestampLabel,
                            );
                            const onTurnClick =
                                onSeek && seconds !== null
                                    ? () => onSeek(seconds)
                                    : undefined;
                            const rowInner = (
                                <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-3 text-sm leading-6">
                                    <span className="text-foreground text-xs font-bold whitespace-nowrap">
                                        {speakerDisplayLabel}
                                    </span>
                                    <div className="min-w-0 space-y-1">
                                        <p className="text-foreground break-words">
                                            {turn.text}
                                        </p>
                                        <TranscriptTurnTimestamp turn={turn} />
                                    </div>
                                </div>
                            );
                            return (
                                <Fragment key={turn.key}>
                                    {turn.isPostTransferStart &&
                                    bridgedTransferPillLabel ? (
                                        <CallEventRow
                                            Icon={PhoneForwarded}
                                            label={bridgedTransferPillLabel}
                                        />
                                    ) : null}
                                    {onTurnClick ? (
                                        <button
                                            className="hover:bg-accent w-full rounded-md px-2 py-1 text-left transition-colors"
                                            onClick={onTurnClick}
                                            title="Jump to this moment in the recording"
                                            type="button"
                                        >
                                            {rowInner}
                                        </button>
                                    ) : (
                                        <div className="px-2 py-1">
                                            {rowInner}
                                        </div>
                                    )}
                                </Fragment>
                            );
                        })}
                        {bridgedTransferFallbackEvent}
                        {failedTransferTimelineEvent}
                        {callEndedTimelineEvent}
                    </div>
                </section>
            ) : viewModel.transcriptText ? (
                <section aria-label="Transcript" className="space-y-3">
                    <SectionHeader Icon={Hash} label="Transcript" />
                    <div className="space-y-4">
                        <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                            {viewModel.transcriptText}
                        </p>
                        {bridgedTransferFallbackEvent}
                        {failedTransferTimelineEvent}
                        {callEndedTimelineEvent}
                    </div>
                </section>
            ) : callEndedTimelineEvent ||
              failedTransferTimelineEvent ||
              bridgedTransferFallbackEvent ? (
                <section aria-label="Transcript" className="space-y-3">
                    <SectionHeader Icon={Hash} label="Transcript" />
                    {bridgedTransferFallbackEvent}
                    {failedTransferTimelineEvent}
                    {callEndedTimelineEvent}
                </section>
            ) : null}
            {viewModel.translation ? (
                <section className="space-y-2">
                    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                        {`Translated from ${viewModel.translation.source_language_name}`}
                    </p>
                    <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                        {viewModel.translation.translated_text}
                    </p>
                </section>
            ) : null}
        </div>
    );
}
